import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  Trash,
  DownloadSimple,
  File,
  Ticket,
  ArrowSquareOut,
  Code,
  Pause,
  Play,
} from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Text,
  Flex,
  Select,
  TagBadge,
  TagPicker,
  SettingsSection,
  DangerSection,
  DangerAction,
  useToast,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  type TagOption,
  type SelectOption,
} from '@chatsdet/ui';
import type { BrowserViewportPreset } from '@chatsdet/types';
import {
  useTagsQuery,
  useEntityTagsQuery,
  useSetEntityTagsMutation,
  useCreateTagMutation,
  type TagColor,
} from '../../../hooks/useTags';
import { useWorkflowMutations } from '../../../hooks/useWorkflowMutations';
import { useWorkflowImportDownloadUrl, isWorkflowEligible } from '../../../hooks/useWorkflows';
import { useQueryClient } from '@tanstack/react-query';
import { isExpectedClientApiError, toApiError } from '../../../lib/api-error';
import { useAuth } from '../../../contexts/AuthContext';
import { useEligibilityModel } from '../../../hooks/useEligibilityModel';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import { API_URL } from '../../../config';
import { ViewportPicker } from '../../components/ViewportPicker';
import type { WorkflowStatus } from './constants';
import { formatUpdatedAt } from './utils';
import { CapabilityLinkSettings } from './CapabilityLinkSettings';
import { RecordingSourceSettings } from './RecordingSourceSettings';
import { SuiteInclusionSettings } from './SuiteInclusionSettings';
import { PromotionTargetSettings } from './PromotionTargetSettings';
import { WorkflowOutputsSettings } from './WorkflowOutputsSettings';
import {
  WorkflowEnvironmentSettings,
  type EnvConstraints,
  type EnvType,
  type WorkflowEnvironment,
  type WorkflowEnvironmentPassHistory,
} from './WorkflowEnvironmentSettings';

type WorkflowFlowType = 'standard' | 'setup' | 'seed' | 'teardown';

export type WorkflowSource = {
  provider: 'csv' | 'linear' | 'jira' | 'recording' | 'external-test';
  externalKey: string | null;
  url: string | null;
};

interface WorkflowSettingsOverlayProps {
  workflowId: string;
  currentName: string;
  currentDescription: string | null;
  currentPreflight: string | null;
  currentCleanup: string | null;
  currentStatus: WorkflowStatus;
  /** Manual pause toggle (set = paused, excluded from suite runs). */
  currentPaused: boolean;
  /** Write-once "has ever passed" timestamp — with `currentStatus` derives eligibility. */
  firstPassedAt: string | null;
  /** Legacy promotion axis (eligibility flag OFF): separate from approval status. */
  currentPromotion: 'promoted' | 'paused' | null;
  currentFlowType: WorkflowFlowType;
  currentRunAgentically: boolean;
  currentSeedRunOncePerSuite?: boolean;
  createdBy: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  source: WorkflowSource | null;
  currentEnvironmentConstraints?: EnvConstraints;
  eligibleEnvironmentIds?: string[] | null;
  /** Named environments on the flow's property (for the default-env picker). */
  environments?: WorkflowEnvironment[] | null;
  /** Flow's current per-flow default environment (used by mixed-mode suite runs). */
  currentDefaultEnvironmentId?: string | null;
  /** Saved browser viewport, or null to follow the organization default. */
  currentViewportSize: BrowserViewportPreset | null;
  /** Per-environment pass history ("does this flow pass on this env?"). */
  passByEnvironment?: WorkflowEnvironmentPassHistory[] | null;
  /** The flow's resolved property (raw UUID or `prop_…`), when known — same
   *  value the sandbox lifecycle hook uses (`environmentsPropertyId`). Powers
   *  the Product capabilities section's option list. */
  propertyId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

type EditableWorkflowStatus = 'draft' | 'approved';

const STATUS_OPTIONS: SelectOption[] = [
  { id: 'draft', label: 'Draft' },
  { id: 'approved', label: 'Approved' },
];

const FLOW_TYPE_OPTIONS: SelectOption[] = [
  { id: 'standard', label: 'Standard' },
  { id: 'seed', label: 'Seed' },
  { id: 'teardown', label: 'Teardown' },
];

const toEditableStatus = (status: WorkflowStatus): EditableWorkflowStatus =>
  status === 'approved' ? 'approved' : 'draft';

type EditableFlowType = 'standard' | 'seed' | 'teardown';

const toEditableFlowType = (flowType: WorkflowFlowType): EditableFlowType =>
  flowType === 'setup' ? 'standard' : flowType;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function WorkflowSettingsOverlay({
  workflowId,
  currentName,
  currentDescription,
  currentPreflight,
  currentCleanup,
  currentStatus,
  currentPaused,
  firstPassedAt,
  currentPromotion,
  currentFlowType,
  currentRunAgentically,
  currentSeedRunOncePerSuite = false,
  createdBy,
  approvedBy,
  approvedAt,
  source,
  currentEnvironmentConstraints = null,
  eligibleEnvironmentIds = null,
  environments = null,
  currentDefaultEnvironmentId = null,
  currentViewportSize,
  passByEnvironment = null,
  propertyId = null,
  isOpen,
  onClose,
  onSaved,
}: WorkflowSettingsOverlayProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentOrgId = user?.orgId ?? null;
  // Per-org eligibility model: ON = derived eligibility + Pause/Unpause; OFF = promotion model.
  const eligibilityModel = useEligibilityModel();

  const [title, setTitle] = useState(currentName);
  const [description, setDescription] = useState(currentDescription ?? '');
  const [preflight, setPreflight] = useState(currentPreflight ?? '');
  const [cleanup, setCleanup] = useState(currentCleanup ?? '');
  const [status, setStatus] = useState<EditableWorkflowStatus>(toEditableStatus(currentStatus));
  const [flowType, setFlowType] = useState<EditableFlowType>(toEditableFlowType(currentFlowType));
  const [runAgentically, setRunAgentically] = useState(currentRunAgentically);
  const [seedRunOncePerSuite, setSeedRunOncePerSuite] = useState(currentSeedRunOncePerSuite);
  const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);

  // Environment constraints — editor supports "any" and "envTypes" modes.
  // "only" mode (pinned env ids) is preserved if already set but not editable
  // here; it will get its own picker once the UI can resolve the workflow's
  // property env list.
  const [constraintMode, setConstraintMode] = useState<'any' | 'envTypes' | 'only'>(
    currentEnvironmentConstraints?.mode ?? 'any'
  );
  const [envTypeSelection, setEnvTypeSelection] = useState<EnvType[]>(
    currentEnvironmentConstraints?.mode === 'envTypes'
      ? currentEnvironmentConstraints.types
      : ['permanent']
  );

  // Per-flow default environment (where this flow runs in a mixed-mode suite).
  const [defaultEnvironmentId, setDefaultEnvironmentId] = useState<string | null>(
    currentDefaultEnvironmentId ?? null
  );
  const [viewportSize, setViewportSize] = useState<BrowserViewportPreset | null>(
    currentViewportSize
  );

  const [hasChanges, setHasChanges] = useState(false);

  // Queries
  const tagsQuery = useTagsQuery();
  const entityTagsQuery = useEntityTagsQuery('workflow', workflowId);

  // Mutations
  const {
    updateWorkflowMutation,
    approveWorkflowMutation,
    promoteWorkflowMutation,
    pauseWorkflowMutation,
    unpauseWorkflowMutation,
  } = useWorkflowMutations({
    currentOrgId,
    workflowId,
  });
  const setEntityTagsMutation = useSetEntityTagsMutation();
  const createTagMutation = useCreateTagMutation();

  // Resolve author display name
  const membersQuery = useOrgMembers({ enabled: isOpen && (!!createdBy || !!approvedBy) });
  const authorName = useMemo(() => {
    if (!createdBy) return null;
    const member = (membersQuery.data?.items ?? []).find((m) => m.userId === createdBy);
    return member?.displayName || member?.primaryEmail || null;
  }, [createdBy, membersQuery.data?.items]);
  const approverName = useMemo(() => {
    if (!approvedBy) return null;
    const member = (membersQuery.data?.items ?? []).find((m) => m.userId === approvedBy);
    return member?.displayName || member?.primaryEmail || approvedBy;
  }, [approvedBy, membersQuery.data?.items]);
  const approvedAtLabel = approvedAt ? formatUpdatedAt(approvedAt) : null;

  // Source file download (CSV imports only)
  const importDownload = useWorkflowImportDownloadUrl(workflowId, isOpen);

  // The Details section only has content for workflows with a resolved author
  // or an external source (recording / Linear / Jira / CSV import) — hide it
  // entirely otherwise so we never render an empty section heading.
  const hasDetailsSection =
    !!authorName ||
    !!approverName ||
    source?.provider === 'recording' ||
    source?.provider === 'external-test' ||
    ((source?.provider === 'linear' || source?.provider === 'jira') && !!source.externalKey) ||
    !!(importDownload.data?.ok && importDownload.data.downloadUrl);

  const isSaving =
    updateWorkflowMutation.isPending ||
    approveWorkflowMutation.isPending ||
    setEntityTagsMutation.isPending;

  // Convert API tags to TagOption format
  const allTags: TagOption[] = useMemo(() => {
    const data = tagsQuery.data;
    if (!data?.ok || !data.data) return [];
    return data.data.map((tag) => ({
      id: tag.id,
      name: tag.name,
      color: tag.color,
    }));
  }, [tagsQuery.data]);

  // Initialize selected tags from entity tags
  useEffect(() => {
    if (entityTagsQuery.data?.ok && entityTagsQuery.data.data) {
      const entityTags = entityTagsQuery.data.data.map((tag) => ({
        id: tag.id,
        name: tag.name,
        color: tag.color,
      }));
      setSelectedTags(entityTags);
    }
  }, [entityTagsQuery.data]);

  // Reset title when opened or currentName changes
  useEffect(() => {
    if (isOpen) {
      setTitle(currentName);
      setDescription(currentDescription ?? '');
      setPreflight(currentPreflight ?? '');
      setCleanup(currentCleanup ?? '');
      setStatus(toEditableStatus(currentStatus));
      setFlowType(toEditableFlowType(currentFlowType));
      setRunAgentically(currentRunAgentically);
      setSeedRunOncePerSuite(currentSeedRunOncePerSuite);
      setConstraintMode(currentEnvironmentConstraints?.mode ?? 'any');
      setEnvTypeSelection(
        currentEnvironmentConstraints?.mode === 'envTypes'
          ? currentEnvironmentConstraints.types
          : ['permanent']
      );
      setDefaultEnvironmentId(currentDefaultEnvironmentId ?? null);
      setViewportSize(currentViewportSize);
      setHasChanges(false);
    }
  }, [
    isOpen,
    currentName,
    currentDescription,
    currentPreflight,
    currentCleanup,
    currentStatus,
    currentFlowType,
    currentRunAgentically,
    currentSeedRunOncePerSuite,
    currentEnvironmentConstraints,
    currentDefaultEnvironmentId,
    currentViewportSize,
  ]);

  // Track changes
  useEffect(() => {
    const entityTags = entityTagsQuery.data?.data ?? [];
    const initialTagIds = new Set(entityTags.map((t) => t.id));
    const currentTagIds = new Set(selectedTags.map((t) => t.id));

    const tagsChanged =
      initialTagIds.size !== currentTagIds.size ||
      [...initialTagIds].some((id) => !currentTagIds.has(id));
    const titleChanged = title !== currentName;
    const descriptionChanged = description !== (currentDescription ?? '');
    const preflightChanged = preflight !== (currentPreflight ?? '');
    const cleanupChanged = cleanup !== (currentCleanup ?? '');
    const statusChanged = status !== toEditableStatus(currentStatus);
    const flowTypeChanged = flowType !== toEditableFlowType(currentFlowType);
    const runAgenticallyChanged = runAgentically !== currentRunAgentically;
    const seedRunOncePerSuiteChanged = seedRunOncePerSuite !== currentSeedRunOncePerSuite;

    // Detect env-constraints change. "only" mode is not edited here, so it's
    // only "changed" if the user switches mode away from it.
    const currentMode = currentEnvironmentConstraints?.mode ?? 'any';
    let constraintsChanged = false;
    if (constraintMode !== currentMode) {
      constraintsChanged = true;
    } else if (
      constraintMode === 'envTypes' &&
      currentEnvironmentConstraints?.mode === 'envTypes'
    ) {
      const before = [...currentEnvironmentConstraints.types].sort().join(',');
      const after = [...envTypeSelection].sort().join(',');
      constraintsChanged = before !== after;
    }

    const defaultEnvChanged = defaultEnvironmentId !== (currentDefaultEnvironmentId ?? null);
    const viewportChanged = viewportSize !== currentViewportSize;

    setHasChanges(
      tagsChanged ||
        titleChanged ||
        descriptionChanged ||
        preflightChanged ||
        cleanupChanged ||
        statusChanged ||
        flowTypeChanged ||
        runAgenticallyChanged ||
        seedRunOncePerSuiteChanged ||
        constraintsChanged ||
        defaultEnvChanged ||
        viewportChanged
    );
  }, [
    title,
    currentName,
    description,
    currentDescription,
    preflight,
    currentPreflight,
    cleanup,
    currentCleanup,
    status,
    currentStatus,
    flowType,
    currentFlowType,
    runAgentically,
    currentRunAgentically,
    seedRunOncePerSuite,
    currentSeedRunOncePerSuite,
    selectedTags,
    entityTagsQuery.data,
    constraintMode,
    envTypeSelection,
    currentEnvironmentConstraints,
    defaultEnvironmentId,
    currentDefaultEnvironmentId,
    viewportSize,
    currentViewportSize,
  ]);

  const handleTagsChange = useCallback((tags: TagOption[]) => {
    setSelectedTags(tags);
  }, []);

  const handleCreateTag = useCallback(
    async (name: string, color: TagColor): Promise<TagOption> => {
      const result = await createTagMutation.mutateAsync({ name, color });
      return {
        id: result.tag.id,
        name: result.tag.name,
        color: result.tag.color,
      };
    },
    [createTagMutation]
  );

  const handleSave = useCallback(async () => {
    try {
      // Build a single update payload for all workflow field changes
      const workflowUpdates: Record<string, unknown> = {};
      if (title !== currentName) workflowUpdates.name = title;
      if (description !== (currentDescription ?? '')) {
        const trimmed = description.trim();
        workflowUpdates.description = trimmed.length > 0 ? trimmed : null;
      }
      if (preflight !== (currentPreflight ?? '')) {
        const trimmed = preflight.trim();
        workflowUpdates.preflight = trimmed.length > 0 ? trimmed : null;
      }
      if (cleanup !== (currentCleanup ?? '')) {
        const trimmed = cleanup.trim();
        workflowUpdates.cleanup = trimmed.length > 0 ? trimmed : null;
      }
      // Approval goes through the dedicated endpoint (records approvedBy/approvedAt
      // and enforces draft-only); reverting to draft stays on the generic update,
      // which clears the approval stamps server-side.
      const statusChanged = status !== toEditableStatus(currentStatus);
      const approving = statusChanged && status === 'approved';
      if (statusChanged && !approving) workflowUpdates.status = status;
      if (flowType !== toEditableFlowType(currentFlowType)) workflowUpdates.flowType = flowType;
      if (runAgentically !== currentRunAgentically) {
        workflowUpdates.runAgentically = runAgentically;
      }
      if (seedRunOncePerSuite !== currentSeedRunOncePerSuite) {
        workflowUpdates.seedRunOncePerSuite = seedRunOncePerSuite;
      }

      // Env constraints — only send when the user changed them in this session.
      const currentMode = currentEnvironmentConstraints?.mode ?? 'any';
      if (constraintMode !== currentMode) {
        if (constraintMode === 'any') {
          workflowUpdates.environmentConstraints = null;
        } else if (constraintMode === 'envTypes') {
          workflowUpdates.environmentConstraints = {
            mode: 'envTypes',
            types: envTypeSelection,
          };
        }
        // Note: switching TO 'only' isn't a path in this editor; the user can
        // only leave an existing 'only' config alone or switch away.
      } else if (
        constraintMode === 'envTypes' &&
        currentEnvironmentConstraints?.mode === 'envTypes'
      ) {
        const before = [...currentEnvironmentConstraints.types].sort().join(',');
        const after = [...envTypeSelection].sort().join(',');
        if (before !== after) {
          workflowUpdates.environmentConstraints = {
            mode: 'envTypes',
            types: envTypeSelection,
          };
        }
      }

      // Per-flow default environment — send when changed (null clears it).
      if (defaultEnvironmentId !== (currentDefaultEnvironmentId ?? null)) {
        workflowUpdates.defaultEnvironmentId = defaultEnvironmentId;
      }
      if (viewportSize !== currentViewportSize) {
        workflowUpdates.viewportSize = viewportSize;
      }

      if (Object.keys(workflowUpdates).length > 0) {
        await updateWorkflowMutation.mutateAsync(workflowUpdates);
      }
      if (approving) {
        await approveWorkflowMutation.mutateAsync();
      }

      // Update tags
      const entityTags = entityTagsQuery.data?.data ?? [];
      const initialTagIds = new Set(entityTags.map((t) => t.id));
      const currentTagIds = new Set(selectedTags.map((t) => t.id));
      const tagsChanged =
        initialTagIds.size !== currentTagIds.size ||
        [...initialTagIds].some((id) => !currentTagIds.has(id));

      if (tagsChanged) {
        await setEntityTagsMutation.mutateAsync({
          entityType: 'workflow',
          entityId: workflowId,
          tagIds: selectedTags.map((t) => t.id),
        });
      }

      toast.success('Settings saved');

      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save settings', {
        reportError: !isExpectedClientApiError(error),
      });
    }
  }, [
    title,
    currentName,
    description,
    currentDescription,
    preflight,
    currentPreflight,
    cleanup,
    currentCleanup,
    status,
    currentStatus,
    flowType,
    currentFlowType,
    runAgentically,
    currentRunAgentically,
    seedRunOncePerSuite,
    currentSeedRunOncePerSuite,
    selectedTags,
    workflowId,
    entityTagsQuery.data,
    updateWorkflowMutation,
    approveWorkflowMutation,
    setEntityTagsMutation,
    toast,
    onSaved,
    onClose,
    constraintMode,
    envTypeSelection,
    currentEnvironmentConstraints,
    defaultEnvironmentId,
    currentDefaultEnvironmentId,
    viewportSize,
    currentViewportSize,
  ]);

  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResettingDerivedData, setIsResettingDerivedData] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);

  const handleClearPlaybook = useCallback(async () => {
    setIsResettingDerivedData(true);
    try {
      const response = await fetch(`${API_URL}/workflows/${encodeURIComponent(workflowId)}/reset`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to clear playbook');
      await queryClient.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      toast.success('Playbook cleared');
      onSaved?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to clear playbook');
    } finally {
      setIsResettingDerivedData(false);
    }
  }, [workflowId, queryClient, toast, onSaved]);

  const regenerateFromRecording = useCallback(
    async (guidance: string) => {
      setIsRegenerating(true);
      try {
        const r = await fetch(
          `${API_URL}/org/workflows/${encodeURIComponent(workflowId)}/regenerate-from-recording`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ guidance }),
          }
        );
        if (!r.ok) {
          const body = (await r.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || 'Failed to regenerate');
        }
        toast.success('Regenerating… the flow will update when the agent finishes.');
        queryClient.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
        onClose();
        return true;
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to regenerate');
        return false;
      } finally {
        setIsRegenerating(false);
      }
    },
    [workflowId, toast, queryClient, onClose]
  );

  const regenerateFromExternalTest = useCallback(async () => {
    setIsRegenerating(true);
    try {
      const response = await fetch(
        `${API_URL}/org/workflows/${encodeURIComponent(workflowId)}/regenerate-from-external-test`,
        { method: 'POST', credentials: 'include' }
      );
      if (!response.ok) throw await toApiError(response, 'Failed to regenerate from Selenium');
      toast.success('Regenerating from the original Selenium revision…');
      await queryClient.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to regenerate from Selenium');
    } finally {
      setIsRegenerating(false);
    }
  }, [workflowId, toast, queryClient, onClose]);

  // Confirmation is an in-app dialog, never a native window.confirm: a native
  // confirm() blocks the page main thread, which wedges automated browser
  // sessions (test-suite agents) at the archive step — they can't read or
  // dismiss the native modal and time out in a dialog loop. See doArchive.
  const handleArchive = useCallback(() => {
    setIsArchiveConfirmOpen(true);
  }, []);

  const doArchive = useCallback(async () => {
    setIsArchiveConfirmOpen(false);
    setIsDeleting(true);
    try {
      const r = await fetch(`${API_URL}/workflows/delete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          id: workflowId,
          deletedFrom: 'settings',
          confirmationMethod: 'in_app_dialog',
        }),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to archive workflow');

      toast.success('Flow archived');
      navigate({ to: '/flows' });

      queryClient.removeQueries({ queryKey: ['workflow-detail', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    } catch (error) {
      setIsDeleting(false);
      toast.error(error instanceof Error ? error.message : 'Failed to archive workflow', {
        reportError: !isExpectedClientApiError(error),
      });
    }
  }, [workflowId, toast, navigate, queryClient]);

  const pausePending = pauseWorkflowMutation.isPending || unpauseWorkflowMutation.isPending;
  // Promotion model (flag OFF): promote + pause share a pending state.
  const promotionPending = promoteWorkflowMutation.isPending || pauseWorkflowMutation.isPending;

  const isEligible = isWorkflowEligible({ status: currentStatus, firstPassedAt });
  const eligibilityReason =
    currentStatus !== 'approved'
      ? 'Needs approval'
      : firstPassedAt == null
        ? "Hasn't passed yet"
        : 'Eligible';

  // Promotion model (flag OFF): promote from settings forces past the run gate;
  // the approval gate stays server-side and can never be forced.
  const handlePromote = useCallback(async () => {
    try {
      await promoteWorkflowMutation.mutateAsync({ force: true });
      toast.success('Flow promoted — it will run when test suites run');
      onSaved?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to promote flow', {
        reportError: !isExpectedClientApiError(error),
      });
    }
  }, [promoteWorkflowMutation, toast, onSaved]);

  const handlePause = useCallback(async () => {
    try {
      await pauseWorkflowMutation.mutateAsync();
      toast.success('Flow paused — excluded from suite runs');
      onSaved?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to pause flow', {
        reportError: !isExpectedClientApiError(error),
      });
    }
  }, [pauseWorkflowMutation, toast, onSaved]);

  const handleUnpause = useCallback(async () => {
    try {
      await unpauseWorkflowMutation.mutateAsync();
      toast.success('Flow unpaused — it will run when test suites run');
      onSaved?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to unpause flow', {
        reportError: !isExpectedClientApiError(error),
      });
    }
  }, [unpauseWorkflowMutation, toast, onSaved]);

  if (!isOpen) return null;

  return (
    <>
      {/* Background - slides up, on exit: waits for fade then slides down */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } }}
        exit={{ y: '100%', transition: { duration: 0.25, delay: 0.2, ease: [0.32, 0.72, 0, 1] } }}
        className="absolute inset-0 z-40 bg-background"
        data-workflow-settings-bg
      />

      {/* Close button - top left where gear was */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, transition: { duration: 0.2, delay: 0.25 } }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
        className="absolute top-1.5 left-2 z-50"
      >
        <Button variant="icon" iconOnly tooltip="Close settings" onPress={onClose}>
          <X size={20} />
        </Button>
      </motion.div>

      {/* Settings form - fades in after background, fades out first on exit */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.2 } }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        className="absolute inset-0 z-40 flex justify-center overflow-y-auto p-8"
        data-workflow-settings
      >
        <div className="my-auto w-full max-w-2xl space-y-6 py-4">
          <div>
            <Text weight="semibold" size="lg" className="mb-1">
              Workflow Settings
            </Text>
            <Text size="sm" className="text-muted-foreground">
              Edit the title, status, and tags for this workflow
            </Text>
          </div>

          <SettingsSection title="General" description="Name, intent, and tags for this workflow.">
            {/* Title input */}
            <div className="space-y-2">
              <label htmlFor="workflow-title" className="text-sm font-medium leading-none">
                Title
              </label>
              <input
                id="workflow-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="flex h-10 w-full rounded-md border border-border bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Enter workflow title"
              />
            </div>

            {/* Description input */}
            <div className="space-y-2">
              <label htmlFor="workflow-description" className="text-sm font-medium leading-none">
                Description
              </label>
              <Text size="xs" className="text-muted-foreground">
                What this test is trying to prove. Shown to the agent on every step as the "why"
                behind each instruction.
              </Text>
              <textarea
                id="workflow-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="flex w-full rounded-md border border-border bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y min-h-[88px]"
                placeholder="e.g. Verify that a hub submission can be processed end-to-end and shows up on the recent activity feed."
              />
            </div>

            {/* Tags picker */}
            <TagPicker
              label="Tags"
              placeholder="Search or create tags..."
              tags={allTags}
              selectedTags={selectedTags}
              onChange={handleTagsChange}
              onCreateTag={handleCreateTag}
              isLoading={tagsQuery.isLoading || entityTagsQuery.isLoading}
            />
          </SettingsSection>

          <SettingsSection
            title="Execution"
            description="Status, type, and how this workflow runs."
          >
            {/* Status */}
            <Select
              label="Status"
              items={STATUS_OPTIONS}
              selectedKey={status}
              onSelectionChange={(key) => {
                if (!key) return;
                setStatus(String(key) as EditableWorkflowStatus);
              }}
            />

            <div className="space-y-2">
              <Text size="sm" weight="medium">
                Browser viewport
              </Text>
              <ViewportPicker
                value={viewportSize}
                onChange={setViewportSize}
                allowOrganizationDefault
                aria-label="Default viewport for this workflow"
              />
              <Text size="xs" className="text-muted-foreground">
                Used for runs of this workflow unless a run overrides it. Organization default
                follows the browser setting in organization settings.
              </Text>
            </div>

            {/* Flow Type */}
            <Select
              label="Flow Type"
              items={FLOW_TYPE_OPTIONS}
              selectedKey={flowType}
              onSelectionChange={(key) => {
                if (!key) return;
                setFlowType(String(key) as EditableFlowType);
              }}
            />

            {/* Execution Mode */}
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">Execution mode</label>
              <div className="space-y-3 rounded-md border border-border bg-card p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={runAgentically}
                    onChange={(e) => setRunAgentically(e.target.checked)}
                    className="mt-1"
                  />
                  <div className="flex flex-col">
                    <Text size="sm" weight="medium">
                      Run Agentically
                    </Text>
                    <Text size="xs" className="text-muted-foreground">
                      Always execute with the agent. Cached browser actions are not replayed or
                      written, but the agent can still update this workflow's playbook.
                    </Text>
                  </div>
                </label>
                {runAgentically && (
                  <div className="flex items-center justify-between gap-3 rounded-md bg-muted/50 p-2">
                    <Text size="xs" className="text-muted-foreground">
                      Clear the current playbook when it should be rebuilt from the next agentic
                      run.
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      onPress={() => {
                        void handleClearPlaybook();
                      }}
                      isDisabled={isResettingDerivedData}
                    >
                      {isResettingDerivedData ? 'Clearing...' : 'Clear playbook'}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Seed scope (seed flows only) */}
            {flowType === 'seed' && (
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none">Seed scope</label>
                <div className="space-y-3 rounded-md border border-border bg-card p-3">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={seedRunOncePerSuite}
                      onChange={(e) => setSeedRunOncePerSuite(e.target.checked)}
                      className="mt-1"
                    />
                    <div className="flex flex-col">
                      <Text size="sm" weight="medium">
                        Always run once per suite
                      </Text>
                      <Text size="xs" className="text-muted-foreground">
                        This seed runs once before any dependent flows and its data is shared across
                        the suite. Consuming flows can no longer switch it to per-flow execution,
                        and per-node input overrides are disabled (the seed runs with its input
                        defaults).
                      </Text>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </SettingsSection>

          {eligibilityModel ? (
            // Eligibility model (flag ON): derived eligibility + Pause/Unpause.
            <SettingsSection
              title="Suite inclusion"
              description="Whether this flow runs when test suites run."
            >
              <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-3">
                <div className="flex flex-col gap-1">
                  <Flex align="center" gap="sm">
                    <Text size="sm" weight="medium">
                      Eligibility
                    </Text>
                    <TagBadge
                      name={currentPaused ? 'Paused' : eligibilityReason}
                      color={!currentPaused && isEligible ? 'blue' : 'gray'}
                      hideDot
                    />
                  </Flex>
                  <Text size="xs" className="text-muted-foreground">
                    {currentPaused
                      ? 'Paused — excluded from suite runs until unpaused.'
                      : isEligible
                        ? 'This flow runs as part of test suites.'
                        : currentStatus !== 'approved'
                          ? 'Approve this flow and pass it once to include it in suite runs.'
                          : 'Pass this flow once to include it in suite runs.'}
                  </Text>
                </div>
                {currentPaused ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={() => void handleUnpause()}
                    isDisabled={pausePending}
                  >
                    <Play size={16} />
                    {unpauseWorkflowMutation.isPending ? 'Unpausing…' : 'Unpause'}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={() => void handlePause()}
                    isDisabled={pausePending}
                  >
                    <Pause size={16} />
                    {pauseWorkflowMutation.isPending ? 'Pausing…' : 'Pause'}
                  </Button>
                )}
              </div>
            </SettingsSection>
          ) : (
            // Promotion model (flag OFF): manual Promote / Pause.
            <SuiteInclusionSettings
              currentPromotion={currentPromotion}
              currentStatus={currentStatus}
              isPending={promotionPending}
              isPromoting={promoteWorkflowMutation.isPending}
              isPausing={pauseWorkflowMutation.isPending}
              onPromote={handlePromote}
              onPause={handlePause}
            />
          )}

          <PromotionTargetSettings workflowId={workflowId} />

          <CapabilityLinkSettings workflowId={workflowId} propertyId={propertyId} />

          <WorkflowEnvironmentSettings
            constraintMode={constraintMode}
            envTypeSelection={envTypeSelection}
            currentEnvironmentConstraints={currentEnvironmentConstraints}
            eligibleEnvironmentIds={eligibleEnvironmentIds}
            environments={environments}
            defaultEnvironmentId={defaultEnvironmentId}
            passByEnvironment={passByEnvironment}
            onConstraintModeChange={setConstraintMode}
            onEnvTypeSelectionChange={setEnvTypeSelection}
            onDefaultEnvironmentIdChange={setDefaultEnvironmentId}
          />

          <WorkflowOutputsSettings workflowId={workflowId} isSeedFlow={flowType === 'seed'} />

          <SettingsSection
            title="Documentation"
            description="Setup that must hold before the run, and teardown notes after — descriptive only, not executed."
          >
            {/* Preflight input */}
            <div className="space-y-2">
              <label htmlFor="workflow-preflight" className="text-sm font-medium leading-none">
                Preflight
              </label>
              <Text size="xs" className="text-muted-foreground">
                Markdown bullet list of preconditions — feature flags, seeded data, environment
                constraints — that must hold before this flow can run.
              </Text>
              <textarea
                id="workflow-preflight"
                value={preflight}
                onChange={(e) => setPreflight(e.target.value)}
                rows={4}
                className="flex w-full rounded-md border border-border bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y min-h-[88px]"
                placeholder="- Feature flag `support_inbox_v3` is enabled.&#10;- At least one Support team exists in the test org."
              />
            </div>

            {/* Cleanup input */}
            <div className="space-y-2">
              <label htmlFor="workflow-cleanup" className="text-sm font-medium leading-none">
                Cleanup
              </label>
              <Text size="xs" className="text-muted-foreground">
                Markdown bullet list of teardown actions to take after the run — descriptive only,
                not executed automatically.
              </Text>
              <textarea
                id="workflow-cleanup"
                value={cleanup}
                onChange={(e) => setCleanup(e.target.value)}
                rows={3}
                className="flex w-full rounded-md border border-border bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y min-h-[72px]"
                placeholder="- Delete the created ticket."
              />
            </div>
          </SettingsSection>

          {hasDetailsSection && (
            <SettingsSection
              title="Details"
              description="Author and where this workflow came from."
            >
              {/* Author (read-only) */}
              {authorName && (
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Author</label>
                  <Text size="sm" className="text-muted-foreground">
                    {authorName}
                  </Text>
                </div>
              )}

              {approverName && (
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Approved by</label>
                  <div className="space-y-1">
                    <Text size="sm" className="text-muted-foreground">
                      {approverName}
                    </Text>
                    {approvedAtLabel && (
                      <Text size="xs" className="text-muted-foreground">
                        {approvedAtLabel}
                      </Text>
                    )}
                  </div>
                </div>
              )}

              {source?.provider === 'recording' && (
                <RecordingSourceSettings
                  source={source}
                  isRegenerating={isRegenerating}
                  onRegenerate={regenerateFromRecording}
                />
              )}

              {source?.provider === 'external-test' && source.externalKey && (
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Selenium source</label>
                  <div className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5">
                    <Code size={18} className="shrink-0 text-muted-foreground" />
                    <Text size="sm" className="font-mono truncate flex-1">
                      {source.externalKey.replace('␟', ' · ')}
                    </Text>
                    {source.url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onPress={() => window.open(source.url!, '_blank')}
                      >
                        <ArrowSquareOut size={16} />
                        Open source
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onPress={regenerateFromExternalTest}
                      isDisabled={isRegenerating}
                    >
                      {isRegenerating ? 'Regenerating…' : 'Regenerate'}
                    </Button>
                  </div>
                </div>
              )}

              {(source?.provider === 'linear' || source?.provider === 'jira') &&
                source.externalKey && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none">
                      {source.provider === 'jira' ? 'Jira Issue' : 'Linear Ticket'}
                    </label>
                    <div className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5">
                      <Ticket size={18} className="shrink-0 text-muted-foreground" />
                      <Text size="sm" className="font-mono truncate flex-1">
                        {source.externalKey}
                      </Text>
                      {source.url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onPress={() => window.open(source.url!, '_blank')}
                        >
                          <ArrowSquareOut size={16} />
                          Open
                        </Button>
                      )}
                    </div>
                  </div>
                )}

              {/* Source File (CSV imports only) */}
              {importDownload.data?.ok && importDownload.data.downloadUrl && (
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Source File</label>
                  <div className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5">
                    <File size={18} className="shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <Text size="sm" className="truncate">
                        {importDownload.data.filename}
                      </Text>
                      {importDownload.data.sizeBytes !== null &&
                        importDownload.data.sizeBytes !== undefined && (
                          <Text size="xs" className="text-muted-foreground">
                            {formatFileSize(importDownload.data.sizeBytes)}
                          </Text>
                        )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onPress={() => window.open(importDownload.data!.downloadUrl!, '_blank')}
                    >
                      <DownloadSimple size={16} />
                      Download
                    </Button>
                  </div>
                </div>
              )}
            </SettingsSection>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onPress={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" onPress={handleSave} isDisabled={!hasChanges || isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>

          {/* Danger Zone */}
          <DangerSection>
            <DangerAction
              title="Archive Workflow"
              description="Hide this workflow from lists and skip it in test suites. Historical runs stay accessible; you can restore the workflow later."
            >
              <Button
                variant="destructive"
                size="sm"
                onPress={handleArchive}
                isDisabled={isDeleting}
              >
                <Trash size={16} />
                Archive Workflow
              </Button>
              <DialogTrigger isOpen={isArchiveConfirmOpen} onOpenChange={setIsArchiveConfirmOpen}>
                <span />
                <Dialog title="Archive this workflow?">
                  <DialogContent>
                    <Text size="sm">
                      It will be hidden from lists and won&apos;t run in test suites. Historical
                      runs stay accessible and you can restore it later.
                    </Text>
                  </DialogContent>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onPress={() => setIsArchiveConfirmOpen(false)}
                      isDisabled={isDeleting}
                    >
                      Cancel
                    </Button>
                    <Button variant="destructive" onPress={doArchive} isDisabled={isDeleting}>
                      {isDeleting ? 'Archiving…' : 'Archive'}
                    </Button>
                  </DialogFooter>
                </Dialog>
              </DialogTrigger>
            </DangerAction>
          </DangerSection>
        </div>
      </motion.div>
    </>
  );
}
